import { BoardCalendarAssetEntrySource } from '../constants';
import { assertNever } from 'owa-assert';
import { lazyDeleteCalendarEventWithConfirm } from 'owa-calendar-event-actions-with-confirm';
import {
    canDelete,
    shouldOpenInCompose,
    canForward,
    canReply,
    isForwardDisabled,
} from 'owa-calendar-event-capabilities';
import { getCalendarEventWithId } from 'owa-calendar-events-store';
import { openItemPeek } from 'owa-calendar-peek';
import type { ClientItemId } from 'owa-client-ids';
import EventScope from 'owa-service/lib/contract/EventScope';
import { useCalendarAssetCallbacks } from 'owa-timestream-asset-sdk-calendars';
import * as React from 'react';
import {
    CalendarEventInteractionCallback,
    isClickCalendarEventCallbackPayload,
    isDoubleClickCalendarEventCallbackPayload,
    isRightClickCalendarEventCallbackPayload,
    isKeyDownCalendarEventCallbackPayload,
    CalendarEventCallbackPayload,
    KeyboardCalendarEventInteraction,
} from 'owa-calendar-event-interaction-schema';
import { forwardSurfaceEvent } from 'owa-calendar-surface-events-common';
import { replyToMeeting } from 'owa-reply-to-meeting';

export function useCalendarEventInteractionCallbacks(
    openEventDetails: (id: ClientItemId) => void,
    selectedDate?: Date,
    onClickCalendarEvent?: (id: string, target?: HTMLElement) => void,
    onRightClickCalendarEvent?: (
        id: ClientItemId,
        parentFolderId: string,
        evt: React.MouseEvent<HTMLDivElement>
    ) => void
): CalendarEventInteractionCallback {
    const callbacks = useCalendarAssetCallbacks();

    return React.useCallback(
        (payload: CalendarEventCallbackPayload) => {
            const { id } = payload;
            if (isClickCalendarEventCallbackPayload(payload)) {
                const target = payload.target;
                const calendarEvent = getCalendarEventWithId(id.Id);
                if (calendarEvent) {
                    if (shouldOpenInCompose(calendarEvent)) {
                        callbacks.onOpenCalendarItemDraft(payload.id.Id);
                    } else {
                        openItemPeek.importAndExecute(calendarEvent);
                    }
                    onClickCalendarEvent?.(id.Id, target);
                }
            } else if (isDoubleClickCalendarEventCallbackPayload(payload)) {
                const calendarEvent = getCalendarEventWithId(id.Id);
                if (calendarEvent) {
                    if (shouldOpenInCompose(calendarEvent)) {
                        callbacks.onOpenCalendarItemDraft(payload.id.Id);
                    } else {
                        callbacks.onOpenCalendarItem(payload.id.Id);
                    }
                }
            } else if (isRightClickCalendarEventCallbackPayload(payload)) {
                const { id, parentFolderId, evt } = payload;
                onRightClickCalendarEvent?.(id, parentFolderId, evt);
            } else if (isKeyDownCalendarEventCallbackPayload(payload)) {
                const { keyboardCalendarEventInteraction, interactionCallback } = payload;
                const item = getCalendarEventWithId(id.Id);

                if (item) {
                    const onConfirm = interactionCallback?.[keyboardCalendarEventInteraction];

                    switch (keyboardCalendarEventInteraction) {
                        case KeyboardCalendarEventInteraction.Delete:
                            if (item && canDelete(item)) {
                                lazyDeleteCalendarEventWithConfirm.importAndExecute(
                                    item,
                                    EventScope.Default,
                                    false /* isItemSeriesMaster */,
                                    BoardCalendarAssetEntrySource,
                                    false /*isInPopOutWindow*/,
                                    true /*disableConfirmationDialog*/,
                                    undefined /* onCancel */,
                                    onConfirm /* onConfirm */
                                );
                            }
                            break;
                        case KeyboardCalendarEventInteraction.Forward:
                            if (canForward(item) && !isForwardDisabled(item)) {
                                forwardSurfaceEvent(
                                    item,
                                    EventScope.ThisInstanceOnly,
                                    openEventDetails
                                );
                            }
                            break;
                        case KeyboardCalendarEventInteraction.ReplyAll:
                            if (canReply(item)) {
                                replyToMeeting(
                                    item,
                                    true /* isReplyAll */,
                                    BoardCalendarAssetEntrySource
                                );
                            }
                            break;
                        case KeyboardCalendarEventInteraction.Reply:
                            if (canReply(item)) {
                                replyToMeeting(
                                    item,
                                    false /* isReplyAll */,
                                    BoardCalendarAssetEntrySource
                                );
                            }
                            break;
                    }
                }
            } else {
                assertNever(payload);
            }
        },
        [selectedDate, onClickCalendarEvent]
    );
}
